// 가격안 비교 시각화 — ops-britzmedi margin/MarginCharts.tsx 이식.
// (변경점: datalabels 플러그인 미사용 — export-desk 에는 chartjs-plugin-datalabels 없음)
// ① 안별 매출 구성(지출 3분해 + 순이익 스택), ② 판매가 스윕 곡선(플로어 꺾임 + 안 마커).
// 팔레트(검증기 4/4 PASS, 고정 순서): 제조원가 #2563eb · 수수료 #d97706 · 운임·기타 #7c3aed · 순이익 #059669.
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import type { CountrySim } from "@/lib/margin/model";
import { effectiveTiers, offerCalc } from "@/lib/margin/model";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

const C = {
  mfg: "#2563eb",
  comm: "#d97706",
  extra: "#7c3aed",
  net: "#059669",
  ink: "#334155",
  muted: "#94a3b8",
  grid: "#e2e8f0",
  surface: "#ffffff",
};

const fmtEok = (v: number) => {
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1e8) return (v / 1e8).toFixed(1) + "억";
  if (abs >= 1e4) return Math.round(v / 1e4).toLocaleString("ko-KR") + "만";
  return Math.round(v).toLocaleString("ko-KR");
};
const fmtKrwFull = (v: number) => Math.round(v).toLocaleString("ko-KR") + "원";

/** ① 안별 매출 구성 스택 바 — 막대 전체 높이 = 총매출, 세그먼트 = 지출 3종 + 순이익. */
export function OfferCompositionChart({ c }: { c: CountrySim }): JSX.Element {
  const calcs = c.offers.map((o) => offerCalc(o, c));
  const labels = c.offers.map((o) => o.label);
  const seg = (label: string, color: string, vals: number[], roundTop = false) => ({
    label,
    data: vals,
    backgroundColor: color,
    borderColor: C.surface,
    borderWidth: 2, // 세그먼트 사이 2px 표면 갭
    maxBarThickness: 56,
    ...(roundTop ? { borderRadius: 4, borderSkipped: "bottom" as const } : {}),
  });
  return (
    <Bar
      data={{
        labels,
        datasets: [
          seg("제조원가(무상분 포함)", C.mfg, calcs.map((r) => r.costMfg)),
          seg("수수료 (판매수수료+차액 인센티브)", C.comm, calcs.map((r) => r.costComm)),
          seg("비용 명목 합", C.extra, calcs.map((r) => r.costCustom)),
          seg("회사 순이익", C.net, calcs.map((r) => r.netKrw), true),
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: { stacked: true, grid: { display: false }, ticks: { color: C.ink, font: { size: 11 } } },
          y: {
            stacked: true,
            grid: { color: C.grid },
            border: { display: false },
            ticks: { color: C.muted, font: { size: 10 }, callback: (v) => fmtEok(Number(v)) },
          },
        },
        plugins: {
          legend: {
            position: "bottom",
            labels: { color: C.ink, boxWidth: 10, boxHeight: 10, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ${fmtKrwFull(ctx.parsed.y ?? NaN)}`,
              footer: (items) => {
                const i = items[0]?.dataIndex ?? 0;
                const r = calcs[i];
                return r ? `총매출 ${fmtKrwFull(r.revenueKrw)} · 순이익률 ${(r.marginPct * 100).toFixed(1)}%` : "";
              },
            },
          },
        },
      }}
    />
  );
}

/** ② 판매가 스윕 — 대당 순이익·수수료 곡선(플로어에서 꺾임) + 현재 가격안 마커. */
export function PriceSweepChart({ c }: { c: CountrySim }): JSX.Element {
  const prices = c.offers
    .flatMap((o) => effectiveTiers(o, c).map((t) => t.price))
    .filter((p) => Number.isFinite(p) && p > 0);
  const lo = Math.max(0, Math.min(c.floorCompany * 0.7 || 0, ...(prices.length ? prices : [0])) - 1_000);
  const hi = Math.max(c.floorCompany * 1.3 || 4_000, ...(prices.length ? prices : [0])) + 1_000;
  const step = Math.max(250, Math.round((hi - lo) / 40 / 250) * 250);
  const xs: number[] = [];
  for (let p = Math.floor(lo / step) * step; p <= hi; p += step) xs.push(p);

  const netAt = (p: number) => {
    const comm = p * c.rate + Math.max(0, p - c.floorCompany);
    return (p - comm) * c.fx - c.costKrw;
  };
  const commAt = (p: number) => (p * c.rate + Math.max(0, p - c.floorCompany)) * c.fx;

  return (
    <Line
      data={{
        labels: xs.map((p) => "$" + p.toLocaleString("en-US")),
        datasets: [
          {
            label: "대당 순이익",
            data: xs.map(netAt),
            borderColor: C.net,
            backgroundColor: C.net,
            borderWidth: 2,
            pointRadius: 0,
            pointHitRadius: 12,
            tension: 0,
          },
          {
            label: "대당 수수료",
            data: xs.map(commAt),
            borderColor: C.comm,
            backgroundColor: C.comm,
            borderWidth: 2,
            borderDash: [5, 4],
            pointRadius: 0,
            pointHitRadius: 12,
            tension: 0,
          },
          {
            label: "구간 단가 위치",
            data: xs.map((p) => {
              const hit = prices.find((tp) => Math.abs(tp - p) < step / 2);
              return hit != null ? netAt(hit) : NaN;
            }),
            showLine: false,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointBackgroundColor: C.mfg,
            pointBorderColor: C.surface, // 겹침 대비 2px 표면 링
            pointBorderWidth: 2,
            borderColor: C.mfg,
            backgroundColor: C.mfg,
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: C.muted, font: { size: 10 }, maxTicksLimit: 8 },
            title: { display: true, text: "판매가 USD/대", color: C.muted, font: { size: 10 } },
          },
          y: {
            grid: { color: C.grid },
            border: { display: false },
            ticks: { color: C.muted, font: { size: 10 }, callback: (v) => fmtEok(Number(v)) },
          },
        },
        plugins: {
          legend: {
            position: "bottom",
            labels: { color: C.ink, boxWidth: 10, boxHeight: 10, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                if (ctx.dataset.label === "구간 단가 위치") {
                  const p = xs[ctx.dataIndex]!;
                  const names = c.offers
                    .filter((o) => effectiveTiers(o, c).some((t) => Math.abs(t.price - p) < step / 2))
                    .map((o) => o.label)
                    .join(", ");
                  return ` ${names || "가격안"}: 대당 순이익 ${fmtKrwFull(ctx.parsed.y ?? NaN)}`;
                }
                return ` ${ctx.dataset.label}: ${fmtKrwFull(ctx.parsed.y ?? NaN)}`;
              },
            },
          },
        },
      }}
    />
  );
}
